package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

// ---------- Hero: dark "trade index" with a search box ----------

/** The only hero on the site on the dark band, so this page reads as a directory. */
val TradesHeroStyle = CssStyle {
    base { Modifier.backgroundColor(RdColor.Band).color(RdColor.BandText).css("padding-block" to "clamp(44px, 6vw, 80px)") }
    cssRule(" .rd-breadcrumb") { Modifier.color(RdColor.BandMuted) }
    cssRule(" .rd-breadcrumb a") { Modifier.color(RdColor.BandMuted) }
    cssRule(" .rd-lede") { Modifier.color(RdColor.BandMuted).css("max-width" to "62ch") }
}

val TradesHeroStackStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(22.px)
}

val TradesHeroTitleStyle = CssStyle.base {
    Modifier.css("max-width" to "18ch")
}

/** Logo-blue highlight word on the dark hero. */
val TradesTitleAccentStyle = CssStyle.base {
    Modifier.color(RdColor.LogoBlue)
}

val TradesFinderStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(12.px).css("max-width" to "720px", "margin-top" to "6px")
}

val TradesFinderLabelStyle = CssStyle.base {
    Modifier.color(RdColor.LogoBlue)
        .css("font" to "500 .74rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase")
}

val TradesSearchStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(12.px).backgroundColor(RdColor.Paper)
            .color(RdColor.Muted).borderRadius(14.px).css("padding" to "6px 6px 6px 18px")
    }
    cssRule(":focus-within") { Modifier.css("outline" to "3px solid rgb(180,198,220)", "outline-offset" to "2px") }
    // The box shows the focus ring; the page-wide :focus-visible ring on the input itself would draw a second one inside it.
    cssRule(" input:focus-visible") { Modifier.css("outline" to "none") }
    cssRule(" input") {
        Modifier.color(RdColor.Ink).css(
            "flex" to "1", "min-width" to "0", "border" to "0", "background" to "transparent", "outline" to "none",
            "font" to "500 1.1rem/1.2 ${RdFont.BODY_STACK}", "padding" to "14px 0"
        )
    }
}

/** The search box's own button, hidden on small phones to leave room for typing. */
val TradesSearchButtonStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.SM { Modifier.display(DisplayStyle.Block) }
    cssRule(" a") { Modifier.css("padding" to "13px 18px", "font-size" to ".95rem") }
}

val TradesSectorsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap") }
    cssRule(" a") {
        Modifier.color(RdColor.BandText).borderRadius(999.px).textDecorationLine(TextDecorationLine.None)
            .css("font-size" to ".9rem", "padding" to "8px 14px", "border" to "1px solid rgba(180,198,220,.4)", "transition" to "border-color .15s, background .15s")
    }
    cssRule(" a:hover") { Modifier.css("border-color" to "rgb(180,198,220)", "background" to "rgba(180,198,220,.12)") }
}

// ---------- Directory ----------

val TradesDirectoryStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(56.px)
}

val TradesGroupStyle = CssStyle {
    base { grid("1fr").css("gap" to "24px 48px", "scroll-margin-top" to "90px") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "240px 1fr") }
}

val TradesGroupHeadStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(8.px).css("align-content" to "start") }
    Breakpoint.LG { Modifier.position(Position.Sticky).css("top" to "calc(env(safe-area-inset-top, 0px) + 92px)") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".95rem") }
}

val TradesGroupTitleStyle = CssStyle.base {
    Modifier.css("font-size" to "clamp(1.4rem, 1.1rem + 1vw, 1.8rem)", "line-height" to 1.1)
}

val TradesGroupCountStyle = CssStyle.base {
    Modifier.color(RdColor.Brand)
        .css("font" to "500 .74rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase")
}

val TradesRowsStyle = CssStyle.base {
    Modifier.css("list-style" to "none", "padding" to "0", "border-top" to "2px solid rgb(30,40,52)", "min-width" to "0")
}

val TradesRowStyle = CssStyle {
    base {
        grid("1fr").css("gap" to "10px 32px", "padding" to "22px 0", "border-bottom" to "1px solid rgb(220,227,236)", "align-items" to "start")
    }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "minmax(0, 1.6fr) minmax(0, 1fr)") }
}

val TradesRowMainStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(8.px).css("min-width" to "0") }
    cssRule(" h3") { Modifier.css("font-size" to "1.25rem") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".97rem", "max-width" to "58ch") }
}

val TradesRowSideStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(8.px).css("justify-items" to "start", "font-size" to ".9rem") }
    cssRule(" > span") {
        Modifier.color(RdColor.Muted).css("font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase")
    }
}

/** Plain text link to a suggested design. */
val TradesDesignLinkStyle = CssStyle {
    base { Modifier.color(RdColor.Brand).textDecorationLine(TextDecorationLine.None).css("font-weight" to 600) }
    hover { Modifier.textDecorationLine(TextDecorationLine.Underline).css("text-underline-offset" to "3px") }
}

/** Solid button to a trade's own page. */
val TradesPageLinkStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(8.px).backgroundColor(RdColor.Brand)
            .color(RdColor.BrandInk).borderRadius(10.px).textDecorationLine(TextDecorationLine.None)
            .css("padding" to "10px 14px", "margin-top" to "2px", "font-weight" to 600)
    }
    hover { Modifier.backgroundColor(RdColor.Band) }
}

val TradesEmptyStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(10.px).backgroundColor(RdColor.SkySoft).border(1.px, LineStyle.Solid, RdColor.LogoBlue)
        .borderRadius(16.px).css("padding" to "28px", "justify-items" to "start")
}

// ---------- What trade websites need ----------

val TradesNeedsGridStyle = CssStyle {
    base { grid("1fr").gap(20.px) }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "1fr 1fr") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "repeat(3, 1fr)") }
}

val TradesNeedStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).gap(10.px).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(16.px).css("align-content" to "start", "padding" to "24px")
    }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".96rem") }
}
